import type { AddClipToBoardRequestBody, BoardClipResponse } from "@/client";

export const NEW_CLIP_PLACEHOLDER_ID = "new-clip-id";

export type BoardInfo = {
	id: string | null;
	title: string | null;
};

/** Move-panel trim per clip; `end` undefined means clip runs through full duration. */
export type MoveClipTrim = {
	start: number;
	end: number | undefined;
};

export function isClipUnsaved(
	clip: BoardClipResponse | null,
	board: BoardInfo | null,
): boolean {
	const clipId = clip?.id;
	const sourceBoardId = board?.id;
	return !clipId || clipId === NEW_CLIP_PLACEHOLDER_ID || !sourceBoardId;
}

export type SaveClipFailureReason =
	| "missing_video"
	| "add_failed"
	| "update_failed"
	| "duplicate";

export function saveClipErrorTitle(reason: SaveClipFailureReason): string {
	switch (reason) {
		case "update_failed":
			return "Failed to update board item";
		case "duplicate":
			return "This clip is already in that board";
		default:
			return "Failed to add clip to board";
	}
}

const CLIP_PADDING_SECONDS = 10;

export function clampClipTrim(
	start: number,
	end: number | undefined,
	videoDurationSeconds: number,
): { start: number; end: number } {
	const clipStart = Math.max(0, start);
	const hasDuration =
		Number.isFinite(videoDurationSeconds) && videoDurationSeconds > 0;
	const clipEnd = end ?? (hasDuration ? videoDurationSeconds : clipStart);
	if (!hasDuration) {
		return { start: clipStart, end: Math.max(clipStart, clipEnd) };
	}
	const clampedStart = Math.min(clipStart, videoDurationSeconds);
	return {
		start: clampedStart,
		end: Math.min(Math.max(clampedStart, clipEnd), videoDurationSeconds),
	};
}

/** Match {@link useAddToBoard}: padded source range plus explicit clip trim. */
export function buildAddClipBody(params: {
	videoId: string;
	boardId: string | null;
	moveClipTrim: MoveClipTrim;
	videoDurationSeconds: number;
	context: "askgvt" | "gvtlabs" | "roster";
	projectId?: string;
	agencyId?: string;
	title?: string;
}): AddClipToBoardRequestBody {
	const { start: clipStart, end: clipEnd } = clampClipTrim(
		params.moveClipTrim.start,
		params.moveClipTrim.end,
		params.videoDurationSeconds,
	);
	const start = Math.max(0, clipStart - CLIP_PADDING_SECONDS);
	const end = Math.min(
		params.videoDurationSeconds > 0
			? params.videoDurationSeconds
			: clipEnd + CLIP_PADDING_SECONDS,
		clipEnd + CLIP_PADDING_SECONDS,
	);

	const body: AddClipToBoardRequestBody = {
		video_id: params.videoId,
		context: params.context,
		start,
		end,
		clipped_start: clipStart,
		clipped_end: clipEnd,
		board_id: params.boardId,
		title: params.title,
	};

	if (params.context === "gvtlabs" && params.projectId) {
		body.project_id = params.projectId;
	}

	if (params.context === "roster" && params.agencyId) {
		body.agency_id = params.agencyId;
	}

	return body;
}
